import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  NotificationMethod,
  UserPreference_Notification_Read,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ConfigTable, { ConfigTableEmptyCell } from '~components/Common/ConfigTable';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { FeatureFlags } from '~constants/featureFlags';
import { FormStateChangeProps } from '~formsShared/types';
import { User, UserPreferenceNotification } from '~hooks/useUsers/models';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

export type UserPreferenceNotificationKey = keyof UserPreference_Notification_Read;

type NotificationsLookup = Partial<
  Record<
    UserPreferenceNotificationKey,
    (preference?: UserPreferenceNotification) => NotificationMethod[]
  >
>;

// Notification types that support email
const EMAIL_SUPPORTED_NOTIFICATIONS: Set<UserPreferenceNotificationKey> = new Set([
  'compliance_expiration_warning',
  'job_accepted',
  'job_canceled',
  'job_completed',
  'job_rejected',
  'new_job_request',
  'order_created',
  'project_created',
]);

const IN_APP_MESSAGING_GATED_NOTIFICATIONS: Set<UserPreferenceNotificationKey> = new Set([
  'new_message',
]);

// These carry the message excerpt, which must only ride push, never SMS or email.
const PUSH_ONLY_NOTIFICATIONS: Set<UserPreferenceNotificationKey> = new Set([
  'new_message',
]);

const isMethodSupported = (
  method: NotificationMethod,
  notification: UserPreferenceNotificationKey,
): boolean => {
  if (PUSH_ONLY_NOTIFICATIONS.has(notification)) {
    return method === NotificationMethod.PUSH;
  }
  if (method === NotificationMethod.EMAIL) {
    return EMAIL_SUPPORTED_NOTIFICATIONS.has(notification);
  }
  return true;
};

const notifications: NotificationsLookup = {
  pending_job_reminder: (preference) => preference?.pendingJobReminder ?? [],
  new_job_request: (preference) => preference?.newJobRequest ?? [],
  start_job_reminder: (preference) => preference?.startJobReminder ?? [],
  order_pending_request: (preference) => preference?.orderPendingRequest ?? [],
  order_accepted: (preference) => preference?.orderAccepted ?? [],
  order_rejected: (preference) => preference?.orderRejected ?? [],
  order_canceled: (preference) => preference?.orderCanceled ?? [],
  order_completed: (preference) => preference?.orderCompleted ?? [],
  job_accepted: (preference) => preference?.jobAccepted ?? [],
  job_rejected: (preference) => preference?.jobRejected ?? [],
  job_canceled: (preference) => preference?.jobCanceled ?? [],
  job_completed: (preference) => preference?.jobCompleted ?? [],
  load_completed: (preference) => preference?.loadCompleted ?? [],
  compliance_expiration_warning: (preference) =>
    preference?.complianceExpirationWarning ?? [],
  order_created: (preference) => preference?.orderCreated ?? [],
  project_created: (preference) => preference?.projectCreated ?? [],
  new_message: (preference) => preference?.newMessage ?? [],
};
const notificationMethods = Object.values(NotificationMethod);
const notificationMethodLabel = notificationMethods.reduce(
  (obj, method) => {
    obj[method] = t(`settings.user_profile.notification_methods.${method}`);

    return obj;
  },
  {} as Record<NotificationMethod, string>,
);

const requiredBooleanSchema = yup.bool().required();
const notificationMethodsSchema = yup.object().shape(
  notificationMethods.reduce(
    (obj, method) => {
      obj[method] = requiredBooleanSchema;

      return obj;
    },
    {} as Record<NotificationMethod, typeof requiredBooleanSchema>,
  ),
);
const notificationsSchema = yup
  .object()
  .shape(
    Object.keys(notifications).reduce(
      (obj, notification) => {
        obj[notification as UserPreferenceNotificationKey] =
          notificationMethodsSchema.required();

        return obj;
      },
      {} as Record<UserPreferenceNotificationKey, typeof notificationMethodsSchema>,
    ),
  )
  .required();

type NotificationMethodsDTO = yup.InferType<typeof notificationMethodsSchema>;
export type UserNotificationsPreferenceDTO = yup.InferType<typeof notificationsSchema>;

const setFormDefaultValues = (user?: User) => {
  const defaultValues = Object.entries(notifications).reduce(
    (obj, [notification, getValue]) => {
      const selectedMethods = getValue(user?.preferences?.notifications ?? undefined);
      const notificationKey = notification as UserPreferenceNotificationKey;

      obj[notificationKey] = notificationMethods.reduce((methods, method) => {
        methods[method] =
          isMethodSupported(method, notificationKey) && selectedMethods.includes(method);

        return methods;
      }, {} as NotificationMethodsDTO);

      return obj;
    },
    {} as UserNotificationsPreferenceDTO,
  );

  return defaultValues;
};

export interface UserNotificationsPreferenceHandler {
  onReset?: (user?: User) => void;
  onSubmit?: (callback: (data: UserNotificationsPreferenceDTO) => void) => void;
}

interface UserNotificationsPreferenceFormProps {
  onFormStateChange?: (args: FormStateChangeProps) => void;
  onPreferenceChange?: (data: Partial<UserNotificationsPreferenceDTO>) => Promise<void>;
  user?: User;
}

const UserNotificationsPreferenceForm = forwardRef<
  UserNotificationsPreferenceHandler,
  UserNotificationsPreferenceFormProps
>(function UserNotificationsPreferenceForm(
  { onFormStateChange, onPreferenceChange, user },
  ref,
) {
  const [isSaving, setIsSaving] = useState(false);
  const isInAppMessagingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.inAppMessaging,
  });
  const {
    control,
    formState: { isDirty },
    getValues,
    handleSubmit,
    reset,
    watch,
  } = useForm<UserNotificationsPreferenceDTO>({
    resolver: yupResolver(notificationsSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setFormDefaultValues(user),
  });

  useImperativeHandle(
    ref,
    () => ({
      onReset: (user?: User) => {
        reset(user ? setFormDefaultValues(user) : undefined);
      },
      onSubmit: (callback) => {
        handleSubmit((data) => {
          callback(data);
        })();
      },
    }),
    [],
  );

  useEffect(() => {
    onFormStateChange?.({ isDirty });
  }, [onFormStateChange, isDirty]);

  useEffect(() => {
    if (!onPreferenceChange) return;

    const subscription = watch((_values, { name, type }) => {
      if (type !== 'change' || !name) return;

      const changedNotification = name.split('.')[0] as UserPreferenceNotificationKey;

      const savePreferences = async () => {
        setIsSaving(true);

        try {
          await onPreferenceChange({
            [changedNotification]: getValues(changedNotification),
          });
          reset(getValues());
        } catch {
          reset();
        } finally {
          setIsSaving(false);
        }
      };

      void savePreferences();
    });

    return () => subscription.unsubscribe();
  }, [getValues, onPreferenceChange, reset, watch]);

  const visibleNotifications = useMemo(() => {
    const allNotifications = Object.keys(
      notifications,
    ) as UserPreferenceNotificationKey[];

    if (isInAppMessagingEnabled) {
      return allNotifications;
    }

    return allNotifications.filter(
      (notification) => !IN_APP_MESSAGING_GATED_NOTIFICATIONS.has(notification),
    );
  }, [isInAppMessagingEnabled]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6">{t('settings.user_profile.notifications')}</Typography>
      <ConfigTable
        columnLabels={[
          t('settings.user_profile.notification'),
          ...notificationMethods.map((method) => notificationMethodLabel[method]),
        ]}
      >
        {visibleNotifications.map((notification) => (
          <TableRow key={notification}>
            <TableCell component="th" scope="row">
              {t(`settings.user_profile.notification_keys.${notification}`)}
            </TableCell>
            {notificationMethods.map((method) =>
              isMethodSupported(method, notification) ? (
                <TableCell key={method} align="center">
                  <CheckBoxFormField
                    control={control}
                    disabled={isSaving}
                    name={`${notification}.${method}`}
                    value={getValues(`${notification}.${method}`)}
                  />
                </TableCell>
              ) : (
                <ConfigTableEmptyCell key={method} />
              ),
            )}
          </TableRow>
        ))}
      </ConfigTable>
    </Box>
  );
});

export default UserNotificationsPreferenceForm;
